/*
 Given a branch workspace key, this hooks retrieves its base branch specification and returns the workspace
 status in regard to the base branch. This will allow component to know whether the given workspace is in sync
 or out of sync with the base branch.
 */
import type { WorkspaceKey } from '@festina-life/api'

import { useWorkspaceManagerContext } from '../../workspace/workspaceManagerContext'
import { useConciseBranch } from '../branches/useConciseBranch'

type WorkspaceStatus = 'inSync' | 'outOfSync'

export const useWorkspaceStatus = (
  workspaceKey: WorkspaceKey,
): WorkspaceStatus => {
  const { getWorkspace } = useWorkspaceManagerContext()
  const workspace = getWorkspace(workspaceKey)

  if (!workspace) {
    throw new Error(
      `A workspace could not be found for ${workspaceKey.name} on ${workspaceKey.branchKey.server} with branch ${workspaceKey.branchKey.name}.`,
    )
  }

  const conciseBranch = useConciseBranch({
    server: workspaceKey.branchKey.server,
    branchName: workspaceKey.branchKey.name,
    shouldFallbackToStandard: false,
  })

  const isWorkspaceOutOfSyncWithBaseBranch =
    workspace &&
    workspace.baseChangesetKey !== conciseBranch?.currentChangesetKey

  return isWorkspaceOutOfSyncWithBaseBranch ? 'outOfSync' : 'inSync'
}
